/**
 * Categorical series-color palette for canvas charts.
 *
 * uPlot draws on a canvas and cannot resolve CSS `var(--lg-*)` the way SVG can,
 * so we read the concrete hex values of the `--lg-viz-*` tokens off a live
 * element via `getComputedStyle` at render time. Colors stay defined in
 * tokens.css (single source of truth, AA-vetted on --lg-bg); this module just
 * resolves and cycles them.
 *
 * Assignment is deterministic **by series name** (djb2 hash) so `api` gets the
 * same color on every chart and dashboard tile — the cross-surface consistency
 * the spec (§4.2.4) calls for.
 */

/** Token names of the categorical palette, in order. Mirrors tokens.css. */
export const VIZ_PALETTE_VARS = [
  '--lg-viz-1',
  '--lg-viz-2',
  '--lg-viz-3',
  '--lg-viz-4',
  '--lg-viz-5',
  '--lg-viz-6',
  '--lg-viz-7',
  '--lg-viz-8',
] as const;

/** Fallback hex values if a token can't be resolved (e.g. detached/jsdom). */
const FALLBACK = [
  '#58a6ff',
  '#3fb950',
  '#e3b341',
  '#ff9d96',
  '#d2a8ff',
  '#76e3ea',
  '#ff9bce',
  '#f0a35e',
];

/** Read a single CSS custom property off `el`, trimmed; '' if unavailable. */
export function resolveCssVar(el: Element, varName: string): string {
  if (typeof getComputedStyle !== 'function') return '';
  return getComputedStyle(el).getPropertyValue(varName).trim();
}

/** Resolve the full categorical palette against `el`, falling back per-slot. */
export function resolveVizPalette(el: Element): string[] {
  return VIZ_PALETTE_VARS.map((v, i) => resolveCssVar(el, v) || FALLBACK[i]);
}

/** djb2 hash — stable palette slot for a series name. */
export function paletteIndexForName(name: string, length: number): number {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  return Math.abs(hash) % length;
}

/** Deterministic color for a named series against a resolved palette. */
export function colorForName(name: string, palette: string[]): string {
  if (!palette.length) return FALLBACK[0];
  return palette[paletteIndexForName(name, palette.length)];
}
